import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faAngleRight,
  faAngleLeft,
  faEllipsis,
} from "@fortawesome/free-solid-svg-icons";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import {
  listQuestions,
  deleteQuestion,
} from "../../../store/reducer/questions/questionSlice";

function Questions() {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const {
    loading,
    questions = [],
    totalCount,
  } = useSelector((state) => state.questions);

  const handleSearch = (search = "") => {
    dispatch(listQuestions({ search, page, limit }));
  };
  const debouncedSearch = useCallback(debounce(handleSearch, 800), [questions]);

  const handlePageClick = ({ selected }) => {
    const page = selected + 1;
    setPage(page);
    dispatch(listQuestions({ search, page, limit }));
  };

  const handleDelete = async (id) => {
    await dispatch(deleteQuestion(id)).then((result) => {
      if (result.payload) {
        dispatch(listQuestions({ search, page, limit }));
        toast.success(result.payload.message);
      } else {
        toast.error(result.error.message);
      }
    });
  };

  // Function to truncate text to 50 characters
  const truncateText = (text, maxLength = 50) => {
    if (typeof text !== "string" || text.length <= maxLength) return text;
    return text.slice(0, maxLength) + "...";
  };

  useEffect(() => {
    dispatch(listQuestions({ search, page, limit }));
  }, []);

  return (
    <>
      <div className="block md:flex justify-between mb-[30px]">
        <div className="flex gap-[16px] items-center mb-[32px] md:mb-0">
          <Link to="/setting">
            <div className="bg-[#F8F8F8] rounded-[10px] p-[8px]">
              <img
                className="w-[24px]"
                src={process.env.PUBLIC_URL + "/icons/back.png"}
                alt="back icon"
              />
            </div>
          </Link>
          <div className="flex flex-col">
            <span className="font-Inter font-[400] leading-[16px] text-[#9E9E9E] text-[14px]">
              Settings / Questions
            </span>
            <span className="font-Inter font-[700] leading-[30px] text-[24px]">
              Question Settings
            </span>
          </div>
        </div>
        <div>
          <Link to="/setting/drawing-questions/add">
            <div className="py-[10px] px-[40px] bg-[#F8F8F8] font-[500] font-Inter text-[18px] flex justify-center text-[#17171B] rounded-[12px] border-2 border-[#17171B] w-max">
              <span>Add Question</span>
            </div>
          </Link>
        </div>
      </div>

      <div className="flex flex-col mt-[20px] gap-[40px]">
        <div className="md:p-5 bg-[#F8F8F8] mt-[20px] rounded-l-[12px]">
          <div className="flex flex-col">
            <div className="md:flex justify-between items-center p-[12px]">
              <div className="leading-[30px] font-Mulish font-[700] text-[24px] mb-[8px] md:mb-0">
                <span>Questions List</span>
              </div>
              <div className="relative">
                <input
                  className="block appearance-none w-full md:w-[300px] bg-white border border-gray-200 text-gray-700 py-3 px-4 pl-[60px] rounded-[12px] leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                  type="text"
                  id="search"
                  name="search"
                  placeholder="Search questions..."
                  onChange={(e) => {
                    setSearch(e.target.value);
                    debouncedSearch(e.target.value);
                  }}
                />
                <div className="pointer-events-none absolute inset-y-0 flex items-center px-5 text-gray-700">
                  <img
                    src={process.env.PUBLIC_URL + "/icons/search.png"}
                    alt="search icon"
                  />
                </div>
              </div>
            </div>
            <div className="sm:rounded-lg mt-5 overflow-scroll">
              <table className="w-full text-center">
                <thead className="bg-white">
                  <tr className="rounded-[12px]">
                    <th className="px-6 py-3 font-Inter font-[600] text-[14px] text-[#6D6D6F] leading-[20px] tracking-wider text-nowrap">
                      ID
                    </th>
                    <th className="px-6 py-3 font-Inter font-[600] text-[14px] text-[#6D6D6F] leading-[20px] tracking-wider text-nowrap">
                      Question Text
                    </th>
                    <th className="px-6 py-3 font-Inter font-[600] text-[14px] text-[#6D6D6F] leading-[20px] tracking-wider text-nowrap">
                      Response Type
                    </th>
                    <th className="px-6 py-3 font-Inter font-[600] text-[14px] text-[#6D6D6F] leading-[20px] tracking-wider text-nowrap">
                      Actions
                    </th>
                  </tr>
                </thead>
                {loading ? (
                  <tbody>
                    <tr>
                      <td colSpan="5" className="py-4">
                        <FontAwesomeIcon icon={faSpinner} size="2xl" spin />
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody>
                    {questions.map((question) => (
                      <tr key={question.id} className="text-center">
                        <td className="px-6 py-4 whitespace-nowrap text-[14px] text-[#6D6D6F] font-[600]">
                          {question.id}
                        </td>
                        <td className="px-6 py-4 text-[14px] text-[#6D6D6F]">
                          {truncateText(question.question_text)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-[14px] text-[#6D6D6F]">
                          {question.response_type}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap font-medium">
                          <div className="flex gap-[10px] justify-center">
                            <Link to={`/setting/drawing-questions/edit/${question.id}`}>
                              <img
                                src={process.env.PUBLIC_URL + "/icons/edit.png"}
                                alt="edit"
                                className="w-[40px] bg-teal-100 rounded-lg"
                              />
                            </Link>
                            <span
                              className="cursor-pointer"
                              onClick={() => handleDelete(question.id)}
                            >
                              <img
                                src={process.env.PUBLIC_URL + "/icons/delete.png"}
                                alt="delete"
                                className="w-[40px] bg-red-100 rounded-lg"
                              />
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mt-[20px] gap-[40px] md:p-5 bg-[#F8F8F8] rounded-l-[12px]">
        <div className="hidden md:block font-Inter font-[300] text-nowrap">
          <span>
            Showing <b>{page}</b> of <b>{Math.ceil(totalCount / limit)}</b>{" "}
            pages
          </span>
        </div>
        <div className="w-full flex justify-end">
          <ReactPaginate
            className="react_pagination_ul"
            breakLabel={
              <span className="w-10 h-10 flex justify-center items-center bg-white rounded-md mr-4">
                <FontAwesomeIcon icon={faEllipsis} size="xl" />
              </span>
            }
            nextLabel={
              <span className="w-10 h-10 flex justify-center items-center bg-white rounded-md">
                <FontAwesomeIcon icon={faAngleRight} size="xl" />
              </span>
            }
            onPageChange={handlePageClick}
            pageCount={Math.ceil(totalCount / limit)}
            previousLabel={
              <span className="w-10 h-10 flex justify-center items-center bg-white rounded-md mr-4">
                <FontAwesomeIcon icon={faAngleLeft} size="xl" />
              </span>
            }
            activeClassName="bg-black text-white"
            pageClassName="block hover:bg-black hover:text-white rounded-md mr-4"
            pageLinkClassName="w-10 h-10 flex justify-center items-center"
            containerClassName="flex items-center justify-center"
          />
        </div>
      </div>
    </>
  );
}

export default Questions;